import { useEffect, useState, type ReactNode } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fmtCompactCurrency, imrLimits, paretoOf } from "@/lovable-demo/lib/warranty/calc";
import type { ForecastPoint, SeriesPoint } from "@/lovable-demo/lib/warranty/types";

function useMounted() {
  const [m, setM] = useState(false);
  useEffect(() => setM(true), []);
  return m;
}

function ChartFrame({ height, children }: { height: number; children: ReactNode }) {
  const mounted = useMounted();
  if (!mounted)
    return (
      <div
        style={{ height }}
        className="w-full animate-pulse rounded-md border border-border/60 bg-elevated/40"
      />
    );
  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        {children as never}
      </ResponsiveContainer>
    </div>
  );
}

const axis = {
  stroke: "var(--muted-foreground)",
  fontSize: 11,
  tickLine: false,
  axisLine: false,
} as const;

const tooltipStyle = {
  contentStyle: {
    background: "var(--popover)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    fontSize: 12,
    color: "var(--popover-foreground)",
  },
  labelStyle: { color: "var(--muted-foreground)", fontSize: 11 },
} as const;

export function Sparkline({ data, tone = "1" }: { data: SeriesPoint[]; tone?: "1" | "2" | "4" }) {
  const mounted = useMounted();
  if (!mounted) return <div className="h-full w-full rounded bg-elevated/40" />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={`spark-${tone}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={`var(--chart-${tone})`} stopOpacity={0.5} />
            <stop offset="100%" stopColor={`var(--chart-${tone})`} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Tooltip
          {...tooltipStyle}
          formatter={(v: number) => [v.toFixed(2), "C/1000"]}
          cursor={{ stroke: "var(--border)" }}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={`var(--chart-${tone})`}
          strokeWidth={1.6}
          fill={`url(#spark-${tone})`}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function C1000Chart({
  data,
  target,
  height = 240,
}: {
  data: SeriesPoint[];
  target?: number | undefined;
  height?: number | undefined;
}) {
  return (
    <ChartFrame height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
        <defs>
          <linearGradient id="c1000" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.45} />
            <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis dataKey="month" {...axis} />
        <YAxis {...axis} width={44} />
        <Tooltip {...tooltipStyle} formatter={(v: number) => [v.toFixed(2), "C/1000"]} />
        {target !== undefined && (
          <ReferenceLine
            y={target}
            stroke="var(--chart-4)"
            strokeDasharray="4 4"
            label={{ value: `Target ${target}`, fill: "var(--chart-4)", fontSize: 11 }}
          />
        )}
        <Area
          type="monotone"
          dataKey="value"
          stroke="var(--chart-1)"
          strokeWidth={2}
          fill="url(#c1000)"
        />
      </AreaChart>
    </ChartFrame>
  );
}

export function ImrChart({ data, height = 250 }: { data: SeriesPoint[]; height?: number }) {
  const limits = imrLimits(data);
  return (
    <div>
      <ChartFrame height={height}>
        <LineChart data={limits.points} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis dataKey="month" {...axis} />
          <YAxis {...axis} width={44} />
          <Tooltip
            {...tooltipStyle}
            formatter={(v: number) => [Number(v).toFixed(2), "Individual"]}
          />
          <ReferenceLine
            y={limits.ucl}
            stroke="var(--chart-5)"
            strokeDasharray="5 4"
            label={{ value: `UCL ${limits.ucl.toFixed(2)}`, fill: "var(--chart-5)", fontSize: 11 }}
          />
          <ReferenceLine
            y={limits.mean}
            stroke="var(--chart-4)"
            strokeDasharray="2 3"
            label={{ value: `X̄ ${limits.mean.toFixed(2)}`, fill: "var(--chart-4)", fontSize: 11 }}
          />
          <ReferenceLine
            y={limits.lcl}
            stroke="var(--chart-5)"
            strokeDasharray="5 4"
            label={{ value: `LCL ${limits.lcl.toFixed(2)}`, fill: "var(--chart-5)", fontSize: 11 }}
          />
          <Line
            type="linear"
            dataKey="value"
            stroke="var(--chart-1)"
            strokeWidth={2}
            dot={{ r: 3, fill: "var(--chart-1)" }}
          />
        </LineChart>
      </ChartFrame>
      <p className="mt-2 text-xs text-muted-foreground">
        Moving range chart — MR̄ {limits.mrBar.toFixed(2)}, MR UCL {limits.mrUcl.toFixed(2)}
      </p>
      <ChartFrame height={110}>
        <LineChart data={limits.points} margin={{ top: 6, right: 12, bottom: 0, left: -12 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis dataKey="month" {...axis} />
          <YAxis {...axis} width={44} />
          <Tooltip
            {...tooltipStyle}
            formatter={(v: number) => [Number(v).toFixed(2), "Moving range"]}
          />
          <ReferenceLine y={limits.mrUcl} stroke="var(--chart-5)" strokeDasharray="5 4" />
          <Line
            type="linear"
            dataKey="mr"
            stroke="var(--chart-3)"
            strokeWidth={1.8}
            dot={{ r: 2.5, fill: "var(--chart-3)" }}
            connectNulls
          />
        </LineChart>
      </ChartFrame>
    </div>
  );
}

export function CostForecastChart({
  data,
  height = 250,
}: {
  data: ForecastPoint[];
  height?: number;
}) {
  return (
    <ChartFrame height={height}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis dataKey="month" {...axis} />
        <YAxis {...axis} width={62} tickFormatter={(v: number) => fmtCompactCurrency(v)} />
        <Tooltip
          {...tooltipStyle}
          formatter={(v: number, name: string) => [
            v == null ? "—" : fmtCompactCurrency(Number(v)),
            name === "actual" ? "Actual cost" : "Projected cost",
          ]}
        />
        <Area
          type="monotone"
          dataKey="aiForecast"
          stroke="var(--chart-2)"
          strokeDasharray="5 4"
          strokeWidth={2}
          fill="var(--chart-2)"
          fillOpacity={0.1}
        />
        <Line
          type="monotone"
          dataKey="actual"
          stroke="var(--chart-1)"
          strokeWidth={2.4}
          dot={false}
          connectNulls
        />
      </ComposedChart>
    </ChartFrame>
  );
}

export function ParetoChart({
  items,
  height = 300,
}: {
  items: { label: string; value: number }[];
  height?: number;
}) {
  const data = paretoOf(items);
  return (
    <ChartFrame height={height}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 60, left: -8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis dataKey="label" {...axis} angle={-32} textAnchor="end" interval={0} height={70} />
        <YAxis yAxisId="left" {...axis} width={48} />
        <YAxis
          yAxisId="right"
          orientation="right"
          {...axis}
          width={44}
          domain={[0, 100]}
          tickFormatter={(v: number) => `${v}%`}
        />
        <Tooltip
          {...tooltipStyle}
          formatter={(v: number, name: string) => [
            name === "cumulative" ? `${v}%` : v.toLocaleString(),
            name === "cumulative" ? "Cumulative" : "Claims",
          ]}
        />
        <Bar yAxisId="left" dataKey="value" radius={[3, 3, 0, 0]}>
          {data.map((d, i) => (
            <Cell key={d.label} fill={`var(--chart-${(i % 5) + 1})`} fillOpacity={0.85} />
          ))}
        </Bar>
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="cumulative"
          stroke="var(--chart-2)"
          strokeWidth={2}
          dot={{ r: 3, fill: "var(--chart-2)" }}
        />
      </ComposedChart>
    </ChartFrame>
  );
}

export function WaterfallChart({
  items,
  height = 300,
}: {
  items: { label: string; value: number; kind: "total" | "increase" | "decrease" }[];
  height?: number;
}) {
  let running = 0;
  const data = items.map((item) => {
    if (item.kind === "total") {
      running = item.value;
      return { ...item, base: 0, span: item.value };
    }
    const start = running;
    running += item.value;
    return {
      ...item,
      base: Math.min(start, running),
      span: Math.abs(item.value),
    };
  });
  return (
    <ChartFrame height={height}>
      <BarChart data={data} margin={{ top: 8, right: 12, bottom: 52, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis dataKey="label" {...axis} angle={-25} textAnchor="end" interval={0} height={64} />
        <YAxis {...axis} width={62} tickFormatter={(v: number) => fmtCompactCurrency(v)} />
        <Tooltip
          {...tooltipStyle}
          formatter={(_v: number, _n: string, entry: { payload?: { value?: number } }) => [
            fmtCompactCurrency(Number(entry?.payload?.value ?? 0)),
            "Amount",
          ]}
        />
        <Bar dataKey="base" stackId="w" fill="transparent" />
        <Bar dataKey="span" stackId="w" radius={[3, 3, 0, 0]}>
          {data.map((d) => (
            <Cell
              key={d.label}
              fill={
                d.kind === "total"
                  ? "var(--chart-1)"
                  : d.value < 0
                    ? "var(--chart-4)"
                    : "var(--chart-5)"
              }
              fillOpacity={0.85}
            />
          ))}
        </Bar>
      </BarChart>
    </ChartFrame>
  );
}
